import { Link } from 'react-router-dom';
import {
  FaHome,
  FaCommentDots,
  FaClipboardList,
  FaChevronDown,
  FaUsers,
  FaComments,
  FaRecycle,
  FaChartBar,
  FaBroadcastTower,
  FaTrash,
  FaDollarSign,
  FaRobot,
  FaStethoscope,
  FaLeaf,
  FaChartLine,
} from 'react-icons/fa';
import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

function Sidebar({ isOpen, toggleSidebar }) {
  const { user } = useContext(AuthContext);
  const [openSubmenus, setOpenSubmenus] = useState({});

  const toggleSubmenu = (label) => {
    setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: <FaHome /> },
    { path: '/user/profile', label: 'Profile', icon: <FaUsers /> },

    // ── My AI Activity History — any customer role, points at their own role's route ──
    ...(user?.role === 'farmer'
      ? [
          { path: '/farmer/ai-history', label: 'My AI Activity', icon: <FaRobot /> },
          { path: '/farmer/ai-diagnosis', label: 'AI Disease Diagnosis', icon: <FaStethoscope /> },
          { path: '/farmer/environmental-analysis', label: 'AI Environmental Analysis', icon: <FaLeaf /> },
          { path: '/farmer/productivity-forecast', label: 'AI Productivity Forecast', icon: <FaChartLine /> },
        ]
      : user?.role === 'waste_collector'
      ? [
          { path: '/waste-collector/ai-history', label: 'My AI Activity', icon: <FaRobot /> },
          { path: '/waste-collector/ai-diagnosis', label: 'AI Disease Diagnosis', icon: <FaStethoscope /> },
          { path: '/waste-collector/environmental-analysis', label: 'AI Environmental Analysis', icon: <FaLeaf /> },
          { path: '/waste-collector/productivity-forecast', label: 'AI Productivity Forecast', icon: <FaChartLine /> },
        ]
      : user?.role === 'user'
      ? [
          { path: '/user/ai-history', label: 'My AI Activity', icon: <FaRobot /> },
          { path: '/user/ai-diagnosis', label: 'AI Disease Diagnosis', icon: <FaStethoscope /> },
          { path: '/user/environmental-analysis', label: 'AI Environmental Analysis', icon: <FaLeaf /> },
          { path: '/user/productivity-forecast', label: 'AI Productivity Forecast', icon: <FaChartLine /> },
        ]
      : []),

    // ── Farmer-only ──────────────────────────────────────────────────────────
    ...(user?.role === 'farmer'
      ? [
          { path: '/farmer/waste-management', label: 'Waste Requests', icon: <FaTrash /> },
          { path: '/farmer/monitoring',        label: 'Real-Time Monitoring', icon: <FaBroadcastTower /> },
        ]
      : []),

    // ── Waste-collector-only ─────────────────────────────────────────────────
    ...(user?.role === 'waste_collector'
      ? [
          { path: '/WasteRequests',              label: 'Waste Requests', icon: <FaTrash /> },
          { path: '/waste-collector/my-bids',    label: 'My Bids', icon: <FaHome /> },
        ]
      : []),

    // ── AI Monitoring — company-type "user" accounts only ────────────────────
    // Backend scopes devices to the logged-in account's own token, so a
    // company user only ever sees their own organization's sensor data,
    // the same way an admin sees every farm's data on their own page.
    ...(user?.role === 'user' && user?.userType === 'company'
      ? [{ path: '/user/monitoring', label: 'AI Monitoring', icon: <FaBroadcastTower /> }]
      : []),

    // ── Consultations (user + farmer) ────────────────────────────────────────
    ...(user?.role === 'user' || user?.role === 'farmer'
      ? [{ path: '/user/consultations', label: 'Consultations Request', icon: <FaClipboardList /> }]
      : []),

    // ── User-only ────────────────────────────────────────────────────────────
    ...(user?.role === 'user'
      ? [{ path: '/user/subscription', label: 'Subscription', icon: <FaComments /> }]
      : []),

    // ── Specialist-only ──────────────────────────────────────────────────────
    ...(user?.role === 'specialist'
      ? [{ path: '/user/my-bookings', label: 'My Bookings', icon: <FaRecycle /> }]
      : []),

    // ── Specialist chat (specialist + farmer + user) ──────────────────────────
    ...(user?.role === 'specialist' || user?.role === 'farmer' || user?.role === 'user'
      ? [{ path: '/user/chat/:consultationId', label: 'Specialist Chat', icon: <FaCommentDots /> }]
      : []),

    // ── Waste-collector chat (collector + farmer) ─────────────────────────────
    ...(user?.role === 'waste_collector' || user?.role === 'farmer'
      ? [
          {
            path: '/farmer/chat/farmer-waste-collector/:consultationId',
            label: 'Waste Collector Chat',
            icon: <FaCommentDots />,
          },
        ]
      : []),

    // ── Analytics (booking / specialist) ─────────────────────────────────────
    {
      path: user?.role === 'farmer' ? '/farmer/analytics' : '/user/analytics',
      label: 'Analytics',
      icon: <FaChartBar />,
    },

    // ── Waste Analytics (farmer self-view + collector self-view) ─────────────
    ...(user?.role === 'farmer' || user?.role === 'waste_collector'
      ? [
          {
            path: user.role === 'farmer' ? '/farmer/waste-analytics' : '/waste-collector/waste-analytics',
            label: 'Waste Analytics',
            icon: <FaTrash />,
          },
        ]
      : []),

    // ── Subscription Analytics (any logged-in role) ───────────────────────────
    {
      path:
        user?.role === 'farmer'
          ? '/farmer/subscription-analytics'
          : user?.role === 'waste_collector'
          ? '/waste-collector/subscription-analytics'
          : '/user/subscription-analytics',
      label: 'Subscription Analytics',
      icon: <FaDollarSign />,
    },
  ];

  return (
    <>
      <style>{`
        .customer-sidebar-scroll { scrollbar-width: thin; scrollbar-color: #4B5563 transparent; }
        .customer-sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .customer-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .customer-sidebar-scroll::-webkit-scrollbar-thumb { background-color: #4B5563; border-radius: 999px; }
      `}</style>
      <div
        className={`fixed inset-y-0 left-0 bg-white dark:bg-gray-700 transition-all duration-300 ease-in-out z-20 shadow-lg flex flex-col ${
          isOpen ? 'w-64' : 'w-0 lg:w-16'
        } ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-700 shrink-0">
          <h2 className={`text-lg font-bold ${isOpen ? 'block' : 'hidden'}`}>Farming Solution</h2>
        </div>
        <nav className="mt-4 flex-1 overflow-y-auto overflow-x-hidden pb-6 customer-sidebar-scroll">
          {navItems.map((item) => {
            const hasChildren = item.children?.length > 0;
            const isOpenSubmenu = openSubmenus[item.label];

            return (
              <div key={item.path}>
                {hasChildren ? (
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-blue-600 hover:text-white transition duration-200 focus:outline-none"
                    onClick={() => toggleSubmenu(item.label)}
                    title={item.label}
                  >
                    <div className="flex items-center">
                      <span className={`mr-3 text-lg ${isOpen ? 'block' : 'block lg:block max-lg:hidden'}`}>
                        {item.icon}
                      </span>
                      <span className={`${isOpen ? 'block' : 'hidden'}`}>{item.label}</span>
                    </div>
                    {isOpen && (
                      <FaChevronDown
                        className={`transition-transform duration-200 ${isOpenSubmenu ? 'rotate-180' : 'rotate-0'}`}
                      />
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-blue-600 hover:text-white transition duration-200"
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    title={item.label}
                  >
                    <div className="flex items-center">
                      <span className={`mr-3 text-lg ${isOpen ? 'block' : 'block lg:block max-lg:hidden'}`}>
                        {item.icon}
                      </span>
                      <span className={`${isOpen ? 'block' : 'hidden'}`}>{item.label}</span>
                    </div>
                  </Link>
                )}
                {hasChildren && isOpen && isOpenSubmenu && (
                  <div className="ml-10">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-500 hover:text-white transition"
                        onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
}

export default Sidebar;